import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllEdges,
  selectAllNodes,
  selectEditorMode,
  selectEditorMoveNode,
  selectFilteredEdges,
} from '../map/slice/mapSelectors';
import { generateColor } from '../utils/colors';
import { MapTooltip } from './MapTooltip';
import { MapPopup, MapPopupRef } from './MapPopup';
import { EdgePopup } from '../edges/EdgePopup';
import { selectEditState, selectScenario } from '../scenarios/scenarioSlice';
import {
  closestPointOnLine,
  edgeToGeoJson,
  PropertyExtractor,
} from '../utils/geoJsonUtils';
import { EdgeTooltip } from '../edges/EdgeTooltip';
import { useTheme } from '../helpers/hooks';
import { Edge, Operation } from '../types';
import { selectShowEdgeDirections } from '../plotter/plotterSelectors';
import { fromLonLat, toLonLat } from 'ol/proj';
import { mapActions } from './slice/mapSlice';
import { useMapLayer } from '../hooks/useMapLayer';
import { useMapContext, MapEventListener } from './context/MapContext';
import { useAnalytics } from '../hooks/useAnalytics';
import { AnalyticsEvent } from '../analytics';

const getEdgeStyle = () => [
  {
    filter: ['<=', ['get', 'head'], 0],
    style: {
      'stroke-width': 4,
      'stroke-color': ['get', 'color'],
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
    },
  },
  {
    filter: ['>', ['get', 'head'], 0],
    style: {
      'shape-points': 3,
      'shape-radius': 10,
      'shape-fill-color': ['get', 'color'],
      'shape-stroke-color': ['get', 'color'],
      'shape-stroke-width': 4,
      'shape-rotate-with-view': false,
      'shape-displacement': [0, 0],
      'shape-opacity': 1,
      'shape-angle': ['get', 'angle'],
    },
  },
];

const extractGeoJsonProps: PropertyExtractor<Edge> = (
  edge,
  _arrowHead,
  theme,
) => {
  return {
    key: `edge-${edge.id}`,
    id: edge.id,
    color:
      theme === 'light'
        ? generateColor(edge.tsp_id || 0)
        : generateColor(edge.tsp_id || 0, 50, 40),
  };
};

export const MapEdges = ({ zIndex = 1 }) => {
  const analytics = useAnalytics();
  const dispatch = useDispatch();
  const {
    map,
    registerHoverListener,
    unregisterHoverListener,
    registerClickListener,
    unregisterClickListener,
  } = useMapContext();
  const edges = useSelector(selectFilteredEdges);
  const nodes = useSelector(selectAllNodes);
  const allEdges = useSelector(selectAllEdges);
  const showEdgeDirections = useSelector(selectShowEdgeDirections);
  const isEditing = useSelector(selectEditState);
  const scenario = useSelector(selectScenario);
  const editorMoveNode = useSelector(selectEditorMoveNode);
  const editorMode = useSelector(selectEditorMode);

  const popupRef = useRef<MapPopupRef | null>(null);

  const [tooltipEdge, setTooltipEdge] = useState<Edge | null>(null);

  const [popupEdge, setPopupEdge] = useState<Edge | null>(null);

  const [theme] = useTheme();

  const isScenarioPage = location.pathname.match(/\/scenarios\/(\d+|new)\/?$/);
  const isMapBuilderPage = location.pathname.match(/\/map-builder/);
  const isMarketConditionsPage = location.pathname === '/';

  const flowsOnEdges = useMemo(() => {
    if (!isScenarioPage || isEditing) {
      return {};
    }
    const flows = scenario.operations?.reduce<Record<string, Operation[]>>(
      (agg, op) => {
        const pathEdgeNodes = op.path_node_ids.slice(0, -1).map((id, i) => {
          return [Number(id), Number(op.path_node_ids[i + 1])];
        });
        pathEdgeNodes.forEach(([startNode, endNode]) => {
          const edge = Object.values(allEdges).find(
            (e) => e.start_node === startNode && e.end_node === endNode,
          );
          if (!edge) return;
          if (!agg[edge.id]) {
            agg[edge.id] = [];
          }
          agg[edge.id].push(op);
        });
        return agg;
      },
      {},
    );
    return flows;
  }, [isScenarioPage, allEdges, scenario.operations, isEditing]);

  const hoverListener: MapEventListener = useCallback(
    (feature) => {
      if (feature?.getProperties()?.key === `edge-${tooltipEdge?.id}`) return;
      if (!feature?.getProperties()?.key?.startsWith('edge-')) {
        setTooltipEdge(null);
        return;
      }
      const edgeId = feature?.getProperties()?.id;
      const edge = allEdges?.[edgeId];
      setTooltipEdge(edge);
    },
    [edges],
  );

  const closePopup = useCallback(() => {
    setPopupEdge(null);
    popupRef.current?.hide();
  }, [popupRef, setPopupEdge]);

  const clickListener: MapEventListener = useCallback(
    (feature, coordinate) => {
      if (isMapBuilderPage && editorMode === 'shift') return;
      if (feature?.getProperties()?.key === `edge-${popupEdge?.id}`) return;

      const edgeId =
        feature && feature.getProperties()?.key?.startsWith('edge-')
          ? feature.getProperties()?.id
          : null;
      const edge = allEdges?.[edgeId];
      if (edge) {
        popupRef.current?.show(coordinate);
        analytics.logEvent(AnalyticsEvent.CLICKED_EDGE, {
          edge: JSON.stringify(edge),
        });
      } else {
        popupRef.current?.hide();
      }
      setPopupEdge(edge);
    },
    [edges, isMapBuilderPage, editorMode],
  );

  const addNodeAlongEdge = () => {
    const popupCoordinates = popupRef.current?.getCoordinates();
    if (!popupEdge || !popupCoordinates) return;
    const startNode = nodes[popupEdge.start_node];
    const endNode = nodes[popupEdge.end_node];
    if (!startNode || !endNode) return;
    const newNodeCords = toLonLat(
      closestPointOnLine(popupCoordinates, [
        fromLonLat([Number(startNode.lng), Number(startNode.lat)]),
        fromLonLat([Number(endNode.lng), Number(endNode.lat)]),
      ]),
    );
    dispatch(
      mapActions.setEditorNode({
        lat: newNodeCords[1].toString(),
        lng: newNodeCords[0].toString(),
        splitEdgeId: popupEdge.id,
        tspId: popupEdge.tsp_id,
      }),
    );
    closePopup();
  };

  const features = useMemo(() => {
    return Object.values(edges)
      .filter((edge) => {
        if (edge?.positions?.length < 2) {
          return false;
        }
        if (
          edge.start_node.toString() === editorMoveNode?.toString() ||
          edge.end_node.toString() === editorMoveNode?.toString()
        ) {
          return false;
        }
        return true;
      })
      .flatMap((edge) =>
        edgeToGeoJson(edge, extractGeoJsonProps, showEdgeDirections, {}, theme),
      );
  }, [edges, showEdgeDirections, theme, editorMoveNode]);

  const layerStyle = useMemo(() => {
    return getEdgeStyle();
  }, []);

  useMapLayer(features, {
    zIndex,
    style: layerStyle,
  });

  useEffect(() => {
    if (!map) return;
    registerHoverListener(hoverListener);
    return () => {
      unregisterHoverListener(hoverListener);
    };
  }, [map, hoverListener]);

  useEffect(() => {
    if (!map) return;
    registerClickListener(clickListener);
    return () => {
      unregisterClickListener(clickListener);
    };
  }, [map, clickListener]);

  return (
    <>
      <MapTooltip key="edges-tooltip" visible={Boolean(tooltipEdge)}>
        <EdgeTooltip
          edge={tooltipEdge}
          showScenarioInfo={Boolean(isScenarioPage)}
          showCapacities={isMarketConditionsPage}
          operations={flowsOnEdges[tooltipEdge?.id ?? ''] ?? []}
        />
      </MapTooltip>
      <MapPopup key="edges-popup" ref={popupRef} onClose={closePopup} autoPan>
        <EdgePopup
          edge={popupEdge}
          operations={flowsOnEdges[popupEdge?.id ?? ''] ?? []}
          showScenarioInfo={Boolean(isScenarioPage)}
          closePopup={closePopup}
          onAddNode={addNodeAlongEdge}
        />
      </MapPopup>
    </>
  );
};
